import { type ChangeEvent, useCallback, useMemo, useState } from 'react'

import { useShallow } from 'zustand/react/shallow'

import { HELP_ITEM_ID } from '@common/components/HelpMenu'
import { useAuthUser } from '@common/hooks/useAuthUser'

import { selectProjectsShellState } from '@modules/projects/stores/projects.selector'
import { useProjectsStore } from '@modules/projects/stores/projects.store'
import { selectCurrentUser, selectCurrentUserAvatarUrl } from '@modules/user/stores/user.selector'
import { useUserStore } from '@modules/user/stores/user.store'

import { createProfileItems, createProjectItems } from '../Header.helpers'
import type { HeaderProps } from '../Header.types'

import { useAvatarImage } from './useAvatarImage'
import { shouldShowProfileAvatar } from './utils/shouldShowProfileAvatar'

export function useHeaderDefault(props: HeaderProps) {
  const {
    profileItems,
    helpItems,
    feedbackDisabled = false,
    onFeedbackPopoverOpenChange,
    onSearchChange,
    feedbackPopoverOpen = false,
    searchDisabled = false,
    helpDisabled = false,
    notificationsDisabled = false,
  } = props

  const { projects, currentProject } = useProjectsStore(useShallow(selectProjectsShellState))
  const currentUser = useUserStore(selectCurrentUser)
  const uploadedAvatarUrl = useUserStore(selectCurrentUserAvatarUrl)
  const userLoading = useUserStore((state) => state.loading)
  const { user: auth0User, isLoading: isAuth0Loading } = useAuthUser()

  const projectItems = useMemo(() => createProjectItems(projects, currentProject), [projects, currentProject])
  const updatedProfileItems = useMemo(() => createProfileItems(profileItems, currentUser), [profileItems, currentUser])

  const avatarSourceUrl = uploadedAvatarUrl ?? auth0User?.picture ?? undefined
  const { src: loadedAvatarSrc, isImagePending: isAvatarImagePending } = useAvatarImage(avatarSourceUrl)

  const showHeaderAvatar = shouldShowProfileAvatar({
    isSessionLoading: isAuth0Loading || userLoading,
    avatarSourceUrl,
    isImagePending: isAvatarImagePending,
  })

  const [searchValue, setSearchValue] = useState('')
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false)

  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    onSearchChange?.('')
  }, [onSearchChange])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target
      setSearchValue(value)
      onSearchChange?.(value)
    },
    [onSearchChange]
  )

  const resolvedHelpItems = useMemo(() => {
    if (!helpItems || !feedbackDisabled) return helpItems

    return helpItems.map((item) =>
      item.type === 'item' && item.id === HELP_ITEM_ID.SHARE_FEEDBACK ? { ...item, active: false } : item
    )
  }, [feedbackDisabled, helpItems])

  const handleFeedbackPopoverOpenChange = useCallback(
    (open: boolean) => {
      if (feedbackDisabled && open) return
      onFeedbackPopoverOpenChange?.(open)
    },
    [feedbackDisabled, onFeedbackPopoverOpenChange]
  )

  const isHelpButtonDisabled = helpDisabled
  const hasHelpItems = !!resolvedHelpItems && resolvedHelpItems.length > 0
  const isHelpDropdownActive = hasHelpItems && !isHelpButtonDisabled

  return {
    currentProject,
    feedbackPopoverOpen: feedbackDisabled ? false : feedbackPopoverOpen,
    handleFeedbackPopoverOpenChange,
    handleSearchChange,
    handleSearchClear,
    hasHelpItems,
    isHelpButtonDisabled,
    isHelpDrawerOpen,
    isHelpDropdownActive,
    loadedAvatarSrc,
    showHeaderAvatar,
    notificationsDisabled,
    projectItems,
    resolvedHelpItems,
    searchDisabled,
    searchValue,
    setIsHelpDrawerOpen,
    updatedProfileItems,
  }
}
